import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/useDebounce";
import { useGeoData, GeoCountry, GeoState, GeoCity } from "@/hooks/useGeoData";

export interface OnboardingPhoto {
  file: File;
  preview: string;
  uploading?: boolean;
}

export interface OnboardingVideo {
  file: File;
  preview: string;
  duration: number;
}

export interface OnboardingData {
  displayName: string;
  username: string;
  bio: string;
  gender: "male" | "female" | "non_binary" | "other" | "prefer_not_to_say" | "";
  countryCode: string;
  stateId: string;
  stateName: string;
  cityId: string;
  cityName: string;
  countryName: string;
  ageConfirmed: boolean;
  // New fields
  country: string;
  nationality: string;
  maritalStatus: string;
  religion: string;
  education: string;
  profession: string;
  annualIncome: string;
  socioeconomicStatus: string;
  lookingFor: string;
  willingToRelocate: string;
  children: string;
}

// Using GeoCountry, GeoState, GeoCity from useGeoData

export const useOnboarding = () => {
  const [photos, setPhotos] = useState<OnboardingPhoto[]>([]);
  const [video, setVideo] = useState<OnboardingVideo | null>(null);
  const [data, setData] = useState<OnboardingData>({
    displayName: "",
    username: "",
    bio: "",
    gender: "",
    countryCode: "",
    stateId: "",
    stateName: "",
    cityId: "",
    cityName: "",
    countryName: "",
    ageConfirmed: false,
    country: "",
    nationality: "",
    maritalStatus: "",
    religion: "",
    education: "",
    profession: "",
    annualIncome: "",
    socioeconomicStatus: "",
    lookingFor: "",
    willingToRelocate: "",
    children: "",
  });
  const [usernameError, setUsernameError] = useState<string>("");
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [countries, setCountries] = useState<GeoCountry[]>([]);
  const [states, setStates] = useState<GeoState[]>([]);
  const [cities, setCities] = useState<GeoCity[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Use useGeoData hook - same as Global Connect
  const { getCountries, getStates, getCities, requiresState, getCountryFlag } = useGeoData();

  const addPhoto = useCallback((file: File) => {
    if (photos.length >= 5) {
      toast({ title: "Maximum 5 photos allowed", variant: "destructive" });
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast({ title: "Please select an image file", variant: "destructive" });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Image must be less than 5MB", variant: "destructive" });
      return;
    }

    const preview = URL.createObjectURL(file);
    setPhotos((prev) => [...prev, { file, preview }]);
  }, [photos.length]);

  const removePhoto = useCallback((index: number) => {
    setPhotos((prev) => {
      const newPhotos = [...prev];
      URL.revokeObjectURL(newPhotos[index].preview);
      newPhotos.splice(index, 1);
      return newPhotos;
    });
  }, []);

  const replacePhoto = useCallback((index: number, file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({ title: "Please select an image file", variant: "destructive" });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Image must be less than 5MB", variant: "destructive" });
      return;
    }

    setPhotos((prev) => {
      const newPhotos = [...prev];
      URL.revokeObjectURL(newPhotos[index].preview);
      newPhotos[index] = { file, preview: URL.createObjectURL(file) };
      return newPhotos;
    });
  }, []);

  const setVideoFile = useCallback((file: File, duration: number) => {
    if (!file.type.startsWith("video/")) {
      toast({ title: "Please select a video file", variant: "destructive" });
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast({ title: "Video must be less than 50MB", variant: "destructive" });
      return;
    }

    if (duration < 5) {
      toast({ title: "Video must be at least 5 seconds", variant: "destructive" });
      return;
    }

    if (duration > 15) {
      toast({ title: "Video must be 15 seconds or less", variant: "destructive" });
      return;
    }

    if (video) {
      URL.revokeObjectURL(video.preview);
    }

    setVideo({ file, preview: URL.createObjectURL(file), duration });
  }, [video]);

  const removeVideo = useCallback(() => {
    if (video) {
      URL.revokeObjectURL(video.preview);
      setVideo(null);
    }
  }, [video]);

  const updateData = useCallback((updates: Partial<OnboardingData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  }, []);

  const fetchCountries = useCallback(async () => {
    setLoadingCountries(true);
    try {
      const countryList = await getCountries();
      setCountries(countryList);
    } catch (error) {
      console.error("Error fetching countries:", error);
      toast({ title: "Error", description: "Failed to load countries", variant: "destructive" });
    } finally {
      setLoadingCountries(false);
    }
  }, [getCountries]);

  const fetchStates = useCallback(async (countryCode: string) => {
    setLoadingStates(true);
    try {
      const stateList = await getStates(countryCode);
      setStates(stateList);
    } catch (error) {
      console.error("Error fetching states:", error);
      toast({ title: "Error", description: "Failed to load states/provinces", variant: "destructive" });
    } finally {
      setLoadingStates(false);
    }
  }, [getStates]);

  const fetchCities = useCallback(async (countryCode: string, stateName?: string) => {
    setLoadingCities(true);
    try {
      const cityList = await getCities(countryCode, stateName);
      setCities(cityList);
    } catch (error) {
      console.error("Error fetching cities:", error);
      toast({ title: "Error", description: "Failed to load cities", variant: "destructive" });
    } finally {
      setLoadingCities(false);
    }
  }, [getCities]);

  const selectCountry = useCallback((code: string, name: string) => {
    updateData({ countryCode: code, countryName: name, country: name, stateId: "", stateName: "", cityId: "", cityName: "" });
    setStates([]);
    setCities([]);
    
    // Check if country requires state/province selection
    if (requiresState(code)) {
      fetchStates(code);
    } else {
      // Fetch cities directly for countries without states
      fetchCities(code);
    }
  }, [requiresState, fetchStates, fetchCities, updateData]);

  const selectState = useCallback((id: string, name: string) => {
    updateData({ stateId: id, stateName: name, cityId: "", cityName: "" });
    setCities([]);
    // Fetch cities using state name (not ID) for useGeoData compatibility
    fetchCities(data.countryCode, name);
  }, [data.countryCode, fetchCities, updateData]);

  const selectCity = useCallback((id: string, name: string) => {
    updateData({ cityId: id, cityName: name });
  }, [updateData]);

  // Username validation
  const validateUsernameFormat = useCallback((username: string): string => {
    if (!username) return "";
    if (username.length < 3) return "Username must be at least 3 characters";
    if (username.length > 20) return "Username must be 20 characters or less";
    if (!/^[a-zA-Z0-9_]+$/.test(username)) return "Only letters, numbers, and underscores allowed";
    return "";
  }, []);

  const checkUsernameAvailability = useCallback(async (username: string): Promise<boolean> => {
    if (!username || username.length < 3) return false;
    
    const formatError = validateUsernameFormat(username);
    if (formatError) {
      setUsernameError(formatError);
      return false;
    }

    setCheckingUsername(true);
    const { data: existing } = await supabase
      .from("profiles")
      .select("id")
      .ilike("username", username)
      .maybeSingle();
    
    setCheckingUsername(false);
    
    if (existing) {
      setUsernameError("Username is already taken");
      return false;
    }
    
    setUsernameError("");
    return true;
  }, [validateUsernameFormat]);

  const updateUsername = useCallback((username: string) => {
    const formatted = username.toLowerCase().replace(/[^a-z0-9_]/g, "");
    updateData({ username: formatted });
    
    const formatError = validateUsernameFormat(formatted);
    if (formatError) {
      setUsernameError(formatError);
    } else {
      setUsernameError("");
    }
  }, [updateData, validateUsernameFormat]);

  const isFemale = data.gender === "female";

  const canSubmit = useCallback(() => {
    // Required: displayName, username, gender, country, city, ageConfirmed
    if (!data.displayName.trim() || data.displayName.trim().length < 2) return false;
    if (!data.username || data.username.length < 3 || data.username.length > 20) return false;
    if (usernameError) return false;
    if (!data.gender) return false;
    if (!data.countryCode) return false;
    if (!data.cityId) return false;
    if (!data.ageConfirmed) return false;
    
    // At least 1 photo
    if (photos.length === 0) return false;

    // Female users need at least 1 video
    if (isFemale && !video) return false;

    return true;
  }, [data, photos.length, video, isFemale, usernameError]);

  const getValidationErrors = useCallback(() => {
    const errors: string[] = [];
    
    if (!data.displayName.trim() || data.displayName.trim().length < 2) {
      errors.push("Name is required (at least 2 characters)");
    }
    if (!data.username || data.username.length < 3) {
      errors.push("Username is required (at least 3 characters)");
    } else if (usernameError) {
      errors.push(usernameError);
    }
    if (!data.gender) errors.push("Gender is required");
    if (!data.countryCode) errors.push("Country is required");
    if (!data.cityId) errors.push("City is required");
    if (!data.ageConfirmed) errors.push("You must confirm you are 18+");
    if (photos.length === 0) errors.push("At least one photo is required");
    if (isFemale && !video) errors.push("A short video is required to continue");

    return errors;
  }, [data, photos.length, video, isFemale, usernameError]);

  const submitOnboarding = useCallback(async (userId: string): Promise<boolean> => {
    if (!canSubmit()) {
      toast({ title: "Please complete all required fields", variant: "destructive" });
      return false;
    }

    setSubmitting(true);

    try {
      // 1. Upload photos
      const uploadedPhotoUrls: string[] = [];
      
      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i];
        const fileExt = photo.file.name.split(".").pop();
        const fileName = `${userId}/${Date.now()}-${i}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("profile-photos")
          .upload(fileName, photo.file, { upsert: true });

        if (uploadError) {
          throw new Error(`Failed to upload photo ${i + 1}: ${uploadError.message}`);
        }

        const { data: urlData } = supabase.storage
          .from("profile-photos")
          .getPublicUrl(fileName);

        uploadedPhotoUrls.push(urlData.publicUrl);

        // Save to profile_photos table
        await supabase.from("profile_photos").insert({
          user_id: userId,
          storage_path: fileName,
          is_primary: i === 0,
          position: i,
        });
      }

      // 2. Upload video if exists
      let videoUrl: string | null = null;
      if (video) {
        const fileExt = video.file.name.split(".").pop() || "mp4";
        const fileName = `${userId}/${Date.now()}.${fileExt}`;

        const { error: videoError } = await supabase.storage
          .from("profile-videos")
          .upload(fileName, video.file, { upsert: true });

        if (videoError) {
          throw new Error(`Failed to upload video: ${videoError.message}`);
        }

        const { data: videoUrlData } = supabase.storage
          .from("profile-videos")
          .getPublicUrl(fileName);

        videoUrl = videoUrlData.publicUrl;

        // Save to profile_videos table
        await supabase.from("profile_videos").insert({
          user_id: userId,
          storage_path: fileName,
          is_intro_video: true,
          duration_seconds: Math.round(video.duration),
        });
      }

      // 3. Update profile
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          name: data.displayName.trim(),
          username: data.username.toLowerCase(),
          bio: data.bio.trim() || null,
          gender: data.gender as any,
          city_id: data.cityId,
          location: `${data.cityName}, ${data.countryName}`,
          photo_url: uploadedPhotoUrls[0],
          video_url: videoUrl,
          has_intro_video: !!videoUrl,
          video_show_pop_balloon: true,
          video_show_global_connect: true,
          video_show_exhibition: true,
          // New fields
          country: data.country.trim() || null,
          nationality: data.nationality.trim() || null,
          marital_status: data.maritalStatus || null,
          religion: data.religion.trim() || null,
          education: data.education.trim() || null,
          profession: data.profession.trim() || null,
          annual_income: data.annualIncome || null,
          socioeconomic_status: data.socioeconomicStatus || null,
          looking_for: data.lookingFor || null,
          willing_to_relocate: data.willingToRelocate || null,
          children: data.children || null,
        })
        .eq("id", userId);

      if (profileError) {
        throw new Error(`Failed to update profile: ${profileError.message}`);
      }

      toast({ title: "Profile created!", description: "Welcome to Pop Balloon" });
      return true;
    } catch (error: any) {
      console.error("Onboarding error:", error);
      toast({ 
        title: "Error creating profile", 
        description: error.message || "Please try again",
        variant: "destructive" 
      });
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, photos, video, data]);

  // Cleanup previews on unmount
  const cleanup = useCallback(() => {
    photos.forEach((p) => URL.revokeObjectURL(p.preview));
    if (video) URL.revokeObjectURL(video.preview);
  }, [photos, video]);

  return {
    // Photos
    photos,
    addPhoto,
    removePhoto,
    replacePhoto,
    
    // Video
    video,
    setVideoFile,
    removeVideo,
    
    // Form data
    data,
    updateData,
    
    // Username
    updateUsername,
    checkUsernameAvailability,
    usernameError,
    checkingUsername,
    
    // Location
    countries,
    states,
    cities,
    fetchCountries,
    fetchStates,
    fetchCities,
    selectCountry,
    selectState,
    selectCity,
    loadingCountries,
    loadingStates,
    loadingCities,
    requiresState,
    getCountryFlag,
    
    // Validation
    isFemale,
    canSubmit,
    getValidationErrors,
    
    // Submit
    submitting,
    submitOnboarding,
    cleanup,
  };
};
